import { isApiErrorResponse, isAxiosServerError, parseError, SERVICE_UNAVAILABLE } from '../../utils/parseError';
import { debounce } from 'lodash-es';
import { SnackbarKey } from 'notistack';
import { NotificationDetails, useNotifications } from './useNotifications';
import { useLocales } from './useLocales';
import { ApiErrors } from '../../utils/apiErrors';
import { ErrorType } from '../../API';

export type ErrorDetails = NotificationDetails;

export type UseHandleErrorReturnType = {
  handleError: (err: unknown, fallback: string, details?: ErrorDetails) => void;
};

export const isForbiddenError = (error: unknown): boolean =>
  isAxiosServerError(error) && error?.response?.status === 403;

const notify = (notifyError: (message: string) => SnackbarKey, message: string) => notifyError(message);
const notifyErrorDebounced = debounce(notify, 500);

export function useHandleError(): UseHandleErrorReturnType {
  const { notifyError } = useNotifications();
  const locales = useLocales();
  const { currentLang, t } = locales;

  const handleError = (error: unknown, fallback: string, details?: ErrorDetails) => {
    if (isAxiosServerError(error)) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const debugInfo = {
        response: {
          status: error?.response?.status,
          statusText: error?.response?.statusText,
          data
        },
        request: {
          url: error?.config?.url,
          method: error?.config?.method,
          params: error?.config?.params,
          data: error?.config?.data
        }
      };

      if (isApiErrorResponse(data)) {
        data.errors?.forEach((apiErr) => {
          let errorMessage: string;
          if (status === SERVICE_UNAVAILABLE.statusCode) {
            errorMessage = t('errors.service_unavailable');
          } else if (typeof status === 'number' && status >= 500) {
            // CT-281: show a safe user message; full payload stays in the notification debug drawer
            errorMessage = t('errors.unexpected');
          } else {
            errorMessage = apiErr.message[currentLang] ?? apiErr.message.en ?? '';
          }
          const errorDetails = ApiErrors[apiErr.type as ErrorType]?.(apiErr, locales, details) || details;
          notifyError(errorMessage, errorDetails, undefined, debugInfo);
        });
      } else {
        if (status === SERVICE_UNAVAILABLE.statusCode) {
          notifyErrorDebounced(notifyError, t('errors.service_unavailable'));
          return;
        }
        let message = parseError(error, currentLang, fallback);
        if (message === SERVICE_UNAVAILABLE.message) {
          notifyErrorDebounced(notifyError, t('errors.service_unavailable'));
          return;
        }
        if (typeof status === 'number' && status >= 500) {
          message = t('errors.unexpected');
        }
        notifyError(message, details, undefined, debugInfo);
      }
    }
  };

  return { handleError };
}
